import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "lib/utils";

/* ══════════════════════════════════════════════════════════════════════════
   Drawer — a side sheet built on Radix Dialog.

   The three campaign mega-modals (create 46 KB, details 43 KB, edit 29 KB)
   each hand-rolled their own overlay, header, close button, scroll body and
   sticky footer. Long, record-shaped content belongs in a side sheet rather
   than a centred modal: it keeps the list visible behind it, gives the form
   full vertical height, and on phones it becomes a bottom sheet, which is
   where a thumb actually reaches.
   ══════════════════════════════════════════════════════════════════════════ */

export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;

type DrawerSize = "sm" | "md" | "lg" | "xl";

const SIZE: Record<DrawerSize, string> = {
    sm: "sm:max-w-md",
    md: "sm:max-w-xl",
    lg: "sm:max-w-3xl",
    xl: "sm:max-w-5xl",
};

export interface DrawerContentProps
    extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
    size?: DrawerSize;
    /** `right` on desktop always becomes a bottom sheet on phones */
    side?: "right" | "bottom";
    showClose?: boolean;
}

export const DrawerContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    DrawerContentProps
>(({ className, children, size = "md", side = "right", showClose = true, ...props }, ref) => (
    <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
            className="fixed inset-0 z-[100] bg-[color:var(--overlay)] backdrop-blur-[2px] animate-overlay-in"
        />
        <DialogPrimitive.Content
            ref={ref}
            onOpenAutoFocus={(e) => e.preventDefault()}
            className={cn(
                "fixed z-[110] flex flex-col bg-surface text-content shadow-overlay",
                // Phone: bottom sheet, capped so the list stays partly visible
                "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl border-t border-line",
                "animate-drawer-in-bottom",
                // Desktop: full-height side panel
                side === "right" &&
                "sm:inset-y-0 sm:right-0 sm:left-auto sm:bottom-auto sm:h-full sm:max-h-none sm:w-full sm:rounded-none sm:rounded-l-2xl sm:border-t-0 sm:border-l sm:animate-drawer-in-right",
                side === "right" && SIZE[size],
                className
            )}
            {...props}
        >
            {/* Grab handle — only meaningful in the bottom-sheet presentation */}
            <div className="sm:hidden flex justify-center pt-2.5 pb-1 flex-shrink-0" aria-hidden="true">
                <span className="w-9 h-1 rounded-full bg-line-strong" />
            </div>

            {children}

            {showClose && (
                <DialogPrimitive.Close
                    className="absolute right-3 top-3 sm:top-4 inline-flex h-9 w-9 items-center justify-center rounded-xl text-content-muted hover:text-content hover:bg-surface-hover transition-colors focus-ring"
                    aria-label="Close panel"
                >
                    <X className="w-4 h-4" />
                </DialogPrimitive.Close>
            )}
        </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
));
DrawerContent.displayName = "DrawerContent";

export const DrawerHeader: React.FC<
    React.HTMLAttributes<HTMLDivElement> & { icon?: React.ReactNode }
> = ({ className, icon, children, ...props }) => (
    <div
        className={cn(
            "flex items-start gap-3 px-4 sm:px-6 pt-3 sm:pt-5 pb-4 border-b border-line flex-shrink-0 pr-14",
            className
        )}
        {...props}
    >
        {icon}
        <div className="min-w-0 flex-1">{children}</div>
    </div>
);

export const DrawerTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn("text-title font-semibold text-content tracking-tight truncate", className)}
        {...props}
    />
));
DrawerTitle.displayName = "DrawerTitle";

export const DrawerDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn("text-meta text-content-muted mt-0.5 leading-relaxed", className)}
        {...props}
    />
));
DrawerDescription.displayName = "DrawerDescription";

/** Scrollable body. `overscroll-contain` stops the page behind from scrolling. */
export const DrawerBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
    className,
    ...props
}) => (
    <div
        className={cn("flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-5 min-h-0", className)}
        style={{ WebkitOverflowScrolling: "touch" }}
        {...props}
    />
);

/** Sticky action bar. Stays reachable no matter how long the body is. */
export const DrawerFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
    className,
    ...props
}) => (
    <div
        className={cn(
            "flex items-center justify-end gap-2 px-4 sm:px-6 py-3.5 border-t border-line bg-surface-muted flex-shrink-0",
            "[&>*]:flex-1 sm:[&>*]:flex-none",
            "pb-[max(0.875rem,env(safe-area-inset-bottom))] sm:pb-3.5",
            className
        )}
        {...props}
    />
);

export default Drawer;
